import {ReadingViewModeEnum} from '@doltech/utils/lib/constants';
import {smoothScroll} from '@doltech/utils/lib/smooth-scroll';
import {get, sum} from 'lodash';
import {Instance, types as t} from 'mobx-state-tree';

import {AbstractTestSection} from '../../question-data-model/TestSection/abstract-test-section.store';
import {TestQuestionGroupStore} from './test-question-group.store';

const ScrollContainerId = 'body-container';

export const TestSectionStore = AbstractTestSection.named('TestSectionStore')
  .props({
    currentQuestionGroupIndex: t.optional(t.number, 0),
    questionGroups: t.array(TestQuestionGroupStore),
    currentViewMode: t.optional(t.number, ReadingViewModeEnum.PASSAGE),
  })
  .views((self) => ({
    get canNext() {
      return self.currentQuestionGroupIndex < self.questionGroups.length - 1;
    },
    get canPrev() {
      return self.currentQuestionGroupIndex > 0;
    },
  }))
  .actions((self) => ({
    next(isScroll = true) {
      if (self.canNext) {
        self.currentQuestionGroupIndex += 1;
        if (isScroll) {
          const questionNo =
            self.questionGroups?.[self.currentQuestionGroupIndex]?.questions?.[0]?.no;
          const elmToScroll = document.getElementById(`question-no-${questionNo}`);
          if (questionNo && elmToScroll) {
            smoothScroll(400, document.getElementById(ScrollContainerId), elmToScroll.offsetTop);
          } else {
            smoothScroll(400, document.getElementById(ScrollContainerId), 0);
          }
        }
      }
    },
    prev(isScroll = true) {
      if (self.canPrev) {
        self.currentQuestionGroupIndex -= 1;
        if (isScroll) {
          const questionGroup = self.questionGroups?.[self.currentQuestionGroupIndex];
          const questionNo = questionGroup?.questions?.[questionGroup?.questions?.length - 1]?.no;
          const elmToScroll = document.getElementById(`question-no-${questionNo}`);
          if (questionNo && elmToScroll) {
            smoothScroll(400, document.getElementById(ScrollContainerId), elmToScroll.offsetTop);
          } else {
            smoothScroll(400, document.getElementById(ScrollContainerId), 0);
          }
        }
      }
    },
    goToQuestion({ questionNo, groupIndex }: { questionNo: number; groupIndex: number }) {
      self.currentQuestionGroupIndex = groupIndex;
      // Only run after currentQuestionGroupIndex change and re-render
      setTimeout(() => {
        const elmToScroll = document.getElementById(`question-no-${questionNo}`);
        const elmContainer = document.getElementById(ScrollContainerId);
        if (elmToScroll) {
          const pos =
            elmContainer.scrollTop +
            elmToScroll.getBoundingClientRect().top -
            elmContainer.getBoundingClientRect().top -
            100;
          smoothScroll(400, elmContainer, pos);
        }
      }, 400);
    },
    updateCurrentViewMode(viewMode) {
      self.currentViewMode = viewMode;
    },
  }))
  .views((self) => ({
    get prevInfo() {
      if (self.canPrev) {
        const { questions } = self.questionGroups[self.currentQuestionGroupIndex - 1];
        const minQuestion = questions[0].no;
        const maxQuestion =
          Number(minQuestion) +
          questions.reduce((p, n) => p + get(n, 'bottomBarQuestionNoes.length', 0), 0) -
          1;
        if (maxQuestion > minQuestion) {
          return `${minQuestion}-${maxQuestion}`;
        }
        return `${minQuestion}`;
      }
      return '';
    },
    get nextInfo() {
      if (self.canNext) {
        const { questions } = self.questionGroups[self.currentQuestionGroupIndex + 1];
        const minQuestion = questions[0].no;
        const maxQuestion =
          Number(minQuestion) +
          questions.reduce((p, n) => p + get(n, 'bottomBarQuestionNoes.length', 0), 0) -
          1;
        if (maxQuestion > minQuestion) {
          return `${minQuestion}-${maxQuestion}`;
        }
        return `${minQuestion}`;
      }
      return '';
    },
    get percent() {
      const answerCount = sum(self.questionGroups.map((group) => get(group, 'answerCount')));
      const questionCount = sum(self.questionGroups.map((group) => get(group, 'questionCount')));

      return (answerCount / questionCount) * 100;
    },

    get totalQuestionInGroup() {
      let count = 0;
      self.questionGroups.forEach((group) => {
        count += get(group, 'questionCount', 0);
      });
      return count;
    },
    get totalAnswerInGroup() {
      let count = 0;
      self.questionGroups.forEach((group) => {
        count += get(group, 'answerCount', 0);
      });
      return count;
    },

    currentQuestionGroup() {
      return self.questionGroups.find(
        (_questionGroup, questionGroupIndex) =>
          questionGroupIndex === self.currentQuestionGroupIndex
      );
    },
  }));

export interface TestSectionStoreInstance extends Instance<typeof TestSectionStore> {}
